import { effect, signal } from '@preact/signals';
import { previewDoc, receiveSandboxMessage, runSequence } from './output';
import { isSandboxMessage } from './sandbox';
import { PREVIEW_CHANNEL, type FromPreviewWindow, type ToPreviewWindow } from './preview-protocol';

/**
 * The HTML preview can leave the output panel for a window of its own (a second screen, side by side with
 * the editor). The window is a Cue page, preview.html, on the same origin; the user's code still runs inside
 * a sandboxed frame with an opaque origin in that page, so it can reach neither Cue nor its storage.
 *
 * Cue sends the window each new preview document; the window relays the frame's console output back.
 * Both sides accept messages only from the other's exact window and from Cue's own origin.
 */

/** True while the preview is shown in its own window instead of the output panel. */
export const previewUndocked = signal(false);
/** The title the window shows, following the active card. */
export const previewTitle = signal('');

let popup: Window | null = null;
let watcher: ReturnType<typeof setInterval> | null = null;
let listening = false;

function send(): void {
  if (!popup || popup.closed) return;
  const message: ToPreviewWindow = { channel: PREVIEW_CHANNEL, type: 'show', doc: previewDoc.peek(), seq: runSequence.peek(), title: previewTitle.peek() };
  popup.postMessage(message, location.origin);
}

function onMessage(event: MessageEvent): void {
  if (!popup || event.source !== popup || event.origin !== location.origin) return;
  const data = event.data as FromPreviewWindow | undefined;
  if (data?.channel !== PREVIEW_CHANNEL) return;
  if (data.type === 'ready') send();
  else if (data.type === 'sandbox' && isSandboxMessage(data.message)) receiveSandboxMessage(data.message);
}

function docked(): void {
  if (watcher) clearInterval(watcher);
  watcher = null;
  popup = null;
  previewUndocked.value = false;
}

function listen(): void {
  if (listening) return;
  listening = true;
  window.addEventListener('message', onMessage);
  // The window has nothing to show without Cue, so it goes when Cue goes.
  window.addEventListener('pagehide', () => popup?.close());
  // Every new document and every title change goes to the window while it is open.
  effect(() => {
    void previewDoc.value;
    void runSequence.value;
    void previewTitle.value;
    if (previewUndocked.value) send();
  });
}

/** Open the preview in its own window, or bring that window to the front. Returns false if the browser blocked it. */
export function undockPreview(): boolean {
  listen();
  if (popup && !popup.closed) {
    popup.focus();
    return true;
  }
  const opened = window.open(new URL('preview.html', location.href), PREVIEW_CHANNEL, 'popup,width=960,height=720');
  if (!opened) return false;
  popup = opened;
  previewUndocked.value = true;
  // A closed window sends nothing reliable on the way out, so watch for it.
  watcher = setInterval(() => {
    if (popup?.closed) docked();
  }, 400);
  return true;
}

/** Close the window and show the preview in the output panel again. */
export function dockPreview(): void {
  popup?.close();
  docked();
}

export function focusPreviewWindow(): void {
  popup?.focus();
}
